import type { DateTime } from 'luxon'

import type { ActivationGroup, ActivationGroupUuid } from '@/features/activationGroup/types'
import { getActivationGroupByUuid } from '@/features/activationGroup/utils/activationGroups'
import { Status } from '@/features/bidding/constants'
import type { Volume } from '@/features/bidding/types/bid'
import type {
  BidOverviewSummaryGroup,
  BidOverviewSummaryRowItem,
  BidSummary,
} from '@/features/bidding/types/bidOverviewSummary'
import { getBidDisplayUnit } from '@/features/bidding/utils/getBidDisplayUnit'

export const buildBidOverviewSummaryRowItem = (
  group: BidOverviewSummaryGroup,
  activationGroups: ActivationGroup[],
): BidOverviewSummaryRowItem | undefined => {
  const capacityBids = Object.values(group.capacityBids)
  const energyBids = Object.values(group.energyBids)

  if (capacityBids.length === 0 && energyBids.length === 0) {
    return
  }

  const uniqueActivationGroupUuids: ActivationGroupUuid[] = [
    ...new Set([...Object.keys(group.capacityBids), ...Object.keys(group.energyBids)]),
  ]

  return {
    portfolio: group.portfolio,
    marketProgram: group.marketProgram,
    deliveryDay: group.deliveryDay,
    createdAt: getLatestCreatedAt([...capacityBids, ...energyBids]),
    activationGroupCodes: resolveActivationGroupCodes(uniqueActivationGroupUuids, activationGroups),
    capacityBid: buildBidCell(capacityBids),
    energyBid: buildBidCell(energyBids),
  }
}

const resolveActivationGroupCodes = (uuids: ActivationGroupUuid[], activationGroups: ActivationGroup[]): string[] =>
  uuids.flatMap((uuid) => {
    const code = getActivationGroupByUuid(activationGroups, uuid)?.code
    if (code === undefined) {
      console.error(`Bid references unknown activation group uuid: ${uuid}`)
      return []
    }
    return [code]
  })

const buildBidCell = (bids: BidSummary[]) => ({
  uniqueStatuses: getUniqueStatuses(bids),
  statusCounts: getStatusCounts(bids),
  totalOfferedVolume: sumVolume(bids, (bid) => bid.offeredVolume),
  totalAcceptedVolume: sumVolume(bids, (bid) => bid.acceptedVolume),
  unit: getUnit(bids),
})

const getLatestCreatedAt = (bids: BidSummary[]): DateTime | undefined => {
  const createdAts = bids.map((bid) => bid.createdAt).filter((createdAt) => createdAt !== undefined)

  return createdAts.toSorted((a, b) => b.valueOf() - a.valueOf())[0]
}

const getUnit = (bids: BidSummary[]): string => {
  if (bids.length === 0) {
    return ''
  }

  return getBidDisplayUnit(bids[0].offeredVolume.unit)
}

const sumVolume = (bids: BidSummary[], pickVolume: (bid: BidSummary) => Volume | undefined): number | null => {
  if (bids.length === 0) {
    return null
  }

  return bids.reduce((total, bid) => total + (pickVolume(bid)?.quantity ?? 0), 0)
}

const getUniqueStatuses = (bids: BidSummary[]) =>
  bids.length === 0 ? [Status.NO_BID] : [...new Set(bids.map((bid) => bid.status))]

const getStatusCounts = (bids: BidSummary[]): Partial<Record<Status, number>> =>
  bids.reduce<Partial<Record<Status, number>>>((counts, bid) => {
    counts[bid.status] = (counts[bid.status] ?? 0) + 1
    return counts
  }, {})
